import { create } from 'zustand';
import { getCoreTokenIdentity, synchronizeTokenIdentity } from './tokenIdentity';
import { loginApi, logoutApi, refreshTokenApi, registerApi } from '../api';
import { LoginCredentials, RegisterPayload, TokenData } from './types';
import { persist } from 'zustand/middleware';
import { registerApiAuthCallbacks, resetApiSession } from '@/shared/api';

interface RefreshOperation {
  generation: number;
  promise: Promise<void>;
}

interface AuthStore {
  clearLocalSession: () => void;
  initAuth: () => void;
  initialized: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  register: (payload: RegisterPayload) => Promise<void>;
  sessionGeneration: number;
  setOrganizationId: (orgId: string | null) => Promise<void>;
  tokenData: TokenData | null;
}

class OrganizationAccessPendingError extends Error {
  constructor() {
    super('Доступ к организации ещё обновляется. Повторите попытку.');
    this.name = 'OrganizationAccessPendingError';
  }
}

let refreshTimerId: ReturnType<typeof setTimeout> | null = null;
let refreshOperation: RefreshOperation | null = null;
let authGeneration = 0;
let authSessionBoundary = 0;
let tokenRevision = 0;
let pendingOrganization: { boundary: number; orgId: string | null } | null = null;

const clearRefreshTimer = (): void => {
  if (refreshTimerId !== null) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
};

const isRefreshTokenValid = (tokenData: TokenData): boolean => {
  return Date.now() < tokenData.obtained_at + tokenData.refresh_expires_in * 1000;
};

const scheduleTokenRefresh = (tokenData: TokenData): void => {
  clearRefreshTimer();
  const delay = tokenData.obtained_at + tokenData.expires_in * 1000 - 20_000 - Date.now();
  refreshTimerId = setTimeout(() => {
    useAuthStore.getState().refreshToken().catch(() => {});
  }, Math.max(0, delay));
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      clearLocalSession: () => {
        authGeneration += 1;
        authSessionBoundary += 1;
        tokenRevision += 1;
        clearRefreshTimer();
        resetApiSession();
        set({ initialized: true, isAuthenticated: false, sessionGeneration: authGeneration, tokenData: null });
      },
      initAuth: () => {
        const { tokenData } = get();
        authGeneration += 1;
        authSessionBoundary += 1;
        tokenRevision += 1;
        resetApiSession();
        if (tokenData && isRefreshTokenValid(tokenData)) {
          try {
            const normalized = synchronizeTokenIdentity(tokenData);
            set({ initialized: true, isAuthenticated: true, sessionGeneration: authGeneration, tokenData: normalized });
            scheduleTokenRefresh(normalized);
            return;
          } catch {
            get().clearLocalSession();
            return;
          }
        }
        clearRefreshTimer();
        set({ initialized: true, isAuthenticated: false, sessionGeneration: authGeneration, tokenData: null });
      },
      initialized: false,
      isAuthenticated: false,
      login: async (credentials) => {
        get().clearLocalSession();
        const generation = authGeneration;
        const tokenData = synchronizeTokenIdentity(await loginApi(credentials));
        if (generation !== authGeneration) throw new Error('Вход отменён. Войдите в аккаунт ещё раз.');
        tokenRevision += 1;
        set({ isAuthenticated: true, tokenData });
        scheduleTokenRefresh(tokenData);
      },
      logout: () => {
        const { tokenData } = get();
        get().clearLocalSession();
        if (tokenData?.refresh_token) logoutApi(tokenData.refresh_token).catch(() => {});
      },
      refreshToken: () => {
        const generation = authGeneration;
        if (refreshOperation?.generation === generation) return refreshOperation.promise;
        const { tokenData } = get();
        if (!tokenData?.refresh_token) {
          get().clearLocalSession();
          return Promise.reject(new Error('Сессия истекла. Войдите в аккаунт ещё раз.'));
        }
        const operation: RefreshOperation = { generation, promise: Promise.resolve() };
        operation.promise = (async (): Promise<void> => {
          try {
            const refreshed = synchronizeTokenIdentity(await refreshTokenApi(tokenData.refresh_token), tokenData);
            if (generation !== authGeneration) throw new Error('Запрос отменён.');
            const identity = getCoreTokenIdentity(refreshed.access_token);
            if (identity && pendingOrganization?.boundary === authSessionBoundary
              && identity.orgId !== pendingOrganization.orgId) {
              set({ tokenData: {
                ...tokenData,
                refresh_expires_in: refreshed.refresh_expires_in
                  + (refreshed.obtained_at - tokenData.obtained_at) / 1000,
                refresh_token: refreshed.refresh_token
              } });
              throw new OrganizationAccessPendingError();
            }
            tokenRevision += 1;
            const hasIdentityChanged = refreshed.user_id !== tokenData.user_id || refreshed.org_id !== tokenData.org_id;
            if (hasIdentityChanged) {
              authGeneration += 1;
              resetApiSession();
            }
            set({ isAuthenticated: true, sessionGeneration: authGeneration, tokenData: refreshed });
            scheduleTokenRefresh(refreshed);
          } catch (error) {
            if (!(error instanceof OrganizationAccessPendingError) && generation === authGeneration) get().logout();
            throw error;
          } finally {
            if (refreshOperation === operation) refreshOperation = null;
          }
        })();
        refreshOperation = operation;
        return operation.promise;
      },
      register: async (payload) => {
        get().clearLocalSession();
        const generation = authGeneration;
        const tokenData = synchronizeTokenIdentity(await registerApi(payload));
        if (generation !== authGeneration) throw new Error('Вход отменён. Войдите в аккаунт ещё раз.');
        tokenRevision += 1;
        set({ isAuthenticated: true, tokenData });
        scheduleTokenRefresh(tokenData);
      },
      sessionGeneration: 0,
      setOrganizationId: async (orgId) => {
        const boundary = authSessionBoundary;
        const previousRefresh = refreshOperation;
        if (previousRefresh?.generation === authGeneration) await previousRefresh.promise;
        if (boundary !== authSessionBoundary) {
          throw new Error('Не удалось обновить доступ к организации. Войдите ещё раз.');
        }
        const activation = { boundary, orgId: orgId?.toLowerCase() ?? null };
        pendingOrganization = activation;
        try {
          await get().refreshToken();
          const { tokenData } = get();
          if (!tokenData || boundary !== authSessionBoundary) {
            throw new Error('Не удалось обновить доступ к организации. Войдите ещё раз.');
          }
          const identity = getCoreTokenIdentity(tokenData.access_token);
          if (identity && identity.orgId !== activation.orgId) {
            throw new OrganizationAccessPendingError();
          }
          if (!identity && tokenData.org_id !== orgId) {
            authGeneration += 1;
            resetApiSession();
            set({ sessionGeneration: authGeneration, tokenData: { ...tokenData, org_id: orgId } });
          }
        } finally {
          if (pendingOrganization === activation) pendingOrganization = null;
        }
      },
      tokenData: null
    }),
    {
      name: 'wl-auth',
      onRehydrateStorage: () => (state) => state?.initAuth(),
      partialize: (state) => ({ tokenData: state.tokenData })
    }
  )
);

registerApiAuthCallbacks({
  clearSession: () => useAuthStore.getState().logout(),
  getAccessToken: () => {
    const state = useAuthStore.getState();
    return state.isAuthenticated ? state.tokenData?.access_token ?? null : null;
  },
  getTokenRevision: () => tokenRevision,
  refreshToken: () => useAuthStore.getState().refreshToken()
});